import type {
  AiModelUsage,
  AnalysisResult,
  DrugInteraction,
  Medicine,
  SafetyIssue,
} from '../types';
import {
  canonicalGenericName,
  enhanceGenericName,
  isKnownGenericName,
  validateDosage,
  validateFrequency,
  validateMedicineName,
} from '../utils/medicineValidator';
import { normalizeBrandName, resolveMedicine, resolveMedicines } from '../utils/medicineResolver';
import {
  enhanceMedicineInstructions,
  enhanceMedicinesWithInstructions,
  hasUsableInstruction,
} from '../utils/prescriptionInstructions';
import { getImtiazAhmedVerifiedMedicines } from '../utils/prescriptionProfiles';
import { extractPatientInfo } from '../utils/patientInfo';
import { getVerifiedLocalInteractions } from '../utils/interactionRules';
import { correctPrescriptionExtraction } from './prescriptionCorrectionService';

interface RiskExplanation {
  reasons: string[];
  tips: string[];
  level: 'safe' | 'caution' | 'danger';
}

function safeString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeMedicine(medicine: Partial<Medicine>, userVerified: boolean): Medicine | null {
  const name = safeString(medicine.name);
  if (!name) return null;

  const validation = validateMedicineName(name);
  const enhanced = enhanceGenericName(name, medicine.generic_name || medicine.genericName);
  const genericKnown = isKnownGenericName(enhanced.genericName);
  const genericConfirmed = userVerified ? genericKnown : enhanced.genericConfirmed;
  const dosage = safeString(medicine.dosage || medicine.strength);
  const frequency = safeString(medicine.frequency);
  const duration = safeString(medicine.duration);
  const validDosage = !dosage || validateDosage(dosage);
  const validFrequency = !frequency || validateFrequency(frequency);

  let confidence = medicine.confidence ?? validation.confidence ?? 0.6;
  if (validation.isValid && validation.confidence >= 0.9) {
    confidence = Math.max(confidence, 0.85);
  }
  if (!validDosage || !validFrequency) {
    confidence *= 0.9;
  }

  const normalizedMedicine: Medicine = {
    ...medicine,
    name,
    generic_name: enhanced.genericName,
    genericName: enhanced.genericName,
    dosage,
    strength: safeString(medicine.strength) || dosage,
    frequency,
    timing: safeString(medicine.timing),
    duration,
    instruction: safeString(medicine.instruction),
    rawInstruction: safeString(medicine.rawInstruction),
    notes: safeString(medicine.notes),
    confidence: Math.min(1, Math.max(0.45, confidence)),
    genericConfirmed,
    verificationStatus: genericKnown ? 'known' : 'needs_verification',
  };

  return enhanceMedicineInstructions(resolveMedicine(normalizedMedicine, userVerified));
}

export function prepareVerifiedMedicines(medicines: Medicine[]): Medicine[] {
  const normalizedMedicines = medicines
    .map((medicine) => normalizeMedicine(medicine, true))
    .filter((medicine): medicine is Medicine => Boolean(medicine));

  return enhanceMedicinesWithInstructions(resolveMedicines(normalizedMedicines, true));
}

function medicineMatchKey(medicine: Medicine): string {
  return normalizeBrandName(medicine.resolvedBrandName || medicine.name)
    .replace(/[^A-Z0-9]/g, '')
    .trim();
}

function applyPrescriptionProfileCorrections(ocrText: string, medicines: Medicine[]): Medicine[] {
  if (medicines.length === 0) return medicines;

  const verifiedProfileMedicines = getImtiazAhmedVerifiedMedicines(ocrText);
  if (!verifiedProfileMedicines) return medicines;

  const correctedByKey = new Map<string, Medicine>();
  verifiedProfileMedicines
    .map((medicine) => normalizeMedicine(medicine, false))
    .filter((medicine): medicine is Medicine => Boolean(medicine))
    .forEach((medicine) => {
      correctedByKey.set(medicineMatchKey(medicine), medicine);
    });

  return medicines.map((medicine) => {
    const correction = correctedByKey.get(medicineMatchKey(medicine));
    if (!correction) return medicine;

    return {
      ...medicine,
      ...correction,
      notes: [medicine.notes, correction.notes].filter(Boolean).join(' '),
      confidence: Math.max(medicine.confidence || 0, correction.confidence || 0),
    };
  });
}

export function resolveExtractedMedicines(ocrText: string, medicines: Medicine[]): Medicine[] {
  const profileCorrectedMedicines = applyPrescriptionProfileCorrections(ocrText, medicines);
  const locallyResolvedMedicines = resolveMedicines(profileCorrectedMedicines, false);
  const correction = correctPrescriptionExtraction(locallyResolvedMedicines, ocrText);
  return enhanceMedicinesWithInstructions(correction.medicines);
}

export async function checkDrugInteractions(
  medicines: Medicine[]
): Promise<{ interactions: DrugInteraction[]; model?: string }> {
  if (medicines.length < 2) {
    return { interactions: [] };
  }

  return { interactions: getVerifiedLocalInteractions(medicines), model: 'Local verified interaction rules' };
}

function getDuplicateIngredientGroups(medicines: Medicine[]): Medicine[][] {
  const groups = new Map<string, Medicine[]>();

  medicines.forEach((medicine) => {
    if (!medicine.genericConfirmed || !isKnownGenericName(medicine.generic_name || medicine.genericName)) {
      return;
    }

    const key = canonicalGenericName(medicine.generic_name || medicine.genericName);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)?.push(medicine);
  });

  return Array.from(groups.values()).filter((group) => group.length > 1);
}

export function generateSafetyIssues(
  medicines: Medicine[],
  interactions: DrugInteraction[]
): SafetyIssue[] {
  const issues: SafetyIssue[] = [];

  getDuplicateIngredientGroups(medicines).forEach((group) => {
    const names = group.map((medicine) => medicine.name);
    issues.push({
      status: 'possible_issue',
      severity: 'high',
      title: 'Confirmed duplicate ingredient',
      description: `${names.join(' and ')} share the same confirmed active ingredient.`,
      recommendation: 'Ask a doctor or pharmacist which one to take. Do not combine duplicate ingredients unless prescribed.',
      medicines: names,
    });
  });

  interactions.forEach((interaction) => {
    const confirmed = interaction.status === 'confirmed_interaction';
    issues.push({
      status: confirmed ? 'confirmed_interaction' : 'possible_issue',
      severity: interaction.severity,
      title: confirmed ? 'Confirmed interaction' : 'Possible interaction',
      description: `${interaction.drug1} and ${interaction.drug2}: ${interaction.description}`,
      recommendation: interaction.recommendation,
      medicines: [interaction.drug1, interaction.drug2],
    });
  });

  medicines.forEach((medicine) => {
    if (medicine.identificationStatus === 'possible_lookalike' && medicine.possibleMatches?.length) {
      issues.push({
        status: 'possible_issue',
        severity: 'low',
        title: 'Possible look-alike name',
        description: `${medicine.name} may match ${medicine.possibleMatches.join(' or ')}. Confirm the exact brand before relying on ingredient checks.`,
        recommendation: 'Compare the prescription with the medicine strip or ask a pharmacist to confirm the exact brand.',
        medicines: [medicine.name],
      });
    }

    if (!isKnownGenericName(medicine.generic_name || medicine.genericName)) {
      issues.push({
        status: 'needs_verification',
        severity: 'low',
        title: 'Needs verification',
        description: `${medicine.name} does not have a confirmed generic or active ingredient.`,
        recommendation: 'Verify the active ingredient from the medicine strip, box, or pharmacist before relying on duplicate checks.',
        medicines: [medicine.name],
      });
    }

    const missingFields = [
      !medicine.dosage && !medicine.strength ? 'dose' : '',
      !medicine.frequency && !medicine.frequencyLabel ? 'frequency' : '',
      !medicine.duration && !medicine.durationLabel ? 'duration' : '',
    ].filter(Boolean);

    if (missingFields.length > 0 && !hasUsableInstruction(medicine)) {
      issues.push({
        status: 'missing_instruction',
        severity: 'low',
        title: 'Missing instruction',
        description: `${medicine.name} is missing ${missingFields.join(', ')} information.`,
        recommendation: 'Confirm the missing instruction with the prescription, doctor, or pharmacist before taking it.',
        medicines: [medicine.name],
      });
    }
  });

  return issues;
}

export function calculateSafetyScore(medicines: Medicine[], interactions: DrugInteraction[]): number {
  const issues = generateSafetyIssues(medicines, interactions);
  let score = 100;

  const hasValue = (value?: string) => {
    const normalized = safeString(value).toLowerCase();
    return Boolean(
      normalized &&
      !['not specified', 'needs review', 'needs verification', 'unknown', 'n/a', 'na'].includes(normalized)
    );
  };

  const confirmedCriticalCount = interactions.filter(
    (interaction) =>
      interaction.status === 'confirmed_interaction' &&
      (interaction.severity === 'critical' || interaction.severity === 'high')
  ).length;
  const confirmedModerateCount = interactions.filter(
    (interaction) => interaction.status === 'confirmed_interaction' && interaction.severity === 'moderate'
  ).length;
  const possibleInteractionCount = interactions.filter(
    (interaction) => interaction.status !== 'confirmed_interaction'
  ).length;
  const duplicateCount = getDuplicateIngredientGroups(medicines).length;
  const reviewCount = medicines.filter(
    (medicine) =>
      medicine.identificationStatus === 'needs_review' ||
      medicine.verificationStatus === 'needs_verification' ||
      !isKnownGenericName(medicine.generic_name || medicine.genericName)
  ).length;
  const medicineCount = medicines.length;
  const reviewRatio = medicineCount > 0 ? reviewCount / medicineCount : 0;
  const possibleIssueCount = issues.filter(
    (issue) => issue.status === 'possible_issue' && issue.title !== 'Confirmed duplicate ingredient'
  ).length;
  const missingCriticalScheduleCount = medicines.filter(
    (medicine) => !hasValue(medicine.dosage) || !hasValue(medicine.frequency) || !hasValue(medicine.timing)
  ).length;
  const missingDurationOnlyCount = medicines.filter(
    (medicine) =>
      hasValue(medicine.dosage) &&
      hasValue(medicine.frequency) &&
      hasValue(medicine.timing) &&
      !hasValue(medicine.duration)
  ).length;
  const lowConfidenceCount = medicines.filter((medicine) => (medicine.confidence ?? 1) < 0.7).length;

  score -= confirmedCriticalCount * 40;
  score -= duplicateCount * 30;
  score -= confirmedModerateCount * 18;
  score -= Math.max(possibleInteractionCount, possibleIssueCount) * 8;
  score -= reviewCount * 6;
  score -= Math.round(reviewRatio * 18);
  score -= missingCriticalScheduleCount * 4;
  score -= missingDurationOnlyCount * 1;
  score -= lowConfidenceCount * 3;

  if (confirmedCriticalCount > 0) score = Math.min(score, 49);
  if (duplicateCount > 0) score = Math.min(score, 69);
  if (confirmedModerateCount > 0) score = Math.min(score, 79);
  if (possibleInteractionCount > 0) score = Math.min(score, 89);
  if (possibleIssueCount > 0) score = Math.min(score, 89);
  if (reviewRatio >= 0.75) score = Math.min(score, 74);
  else if (reviewRatio >= 0.5) score = Math.min(score, 79);
  else if (reviewCount > 0) score = Math.min(score, 86);
  if (missingCriticalScheduleCount > 0) score = Math.min(score, 88);

  if (
    confirmedCriticalCount === 0 &&
    duplicateCount === 0 &&
    confirmedModerateCount === 0 &&
    possibleInteractionCount === 0 &&
    possibleIssueCount === 0 &&
    reviewCount === 0
  ) {
    score = Math.max(score, 94);
  }

  return Math.max(0, Math.min(100, score));
}

export function generateWarnings(issues: SafetyIssue[]): string[] {
  return issues.map((issue) => `${issue.title}: ${issue.description}`);
}

export function getRiskLevel(issues: SafetyIssue[]): 'safe' | 'caution' | 'danger' {
  const hasHighEvidence = issues.some(
    (issue) =>
      issue.title === 'Confirmed duplicate ingredient' ||
      (issue.status === 'confirmed_interaction' &&
        (issue.severity === 'critical' || issue.severity === 'high'))
  );

  if (hasHighEvidence) return 'danger';
  return issues.length > 0 ? 'caution' : 'safe';
}

export function getRiskSummary(analysis: AnalysisResult): {
  level: 'safe' | 'caution' | 'danger';
  title: 'Safe' | 'Caution' | 'High Risk';
  description: string;
  confirmedInteractionsCount: number;
} {
  const issues = analysis.issues || generateSafetyIssues(analysis.medicines, analysis.interactions);
  const level = getRiskLevel(issues);
  const confirmedInteractionsCount = issues.filter((issue) => issue.status === 'confirmed_interaction').length;

  if (level === 'danger') {
    return {
      level,
      title: 'High Risk',
      description:
        confirmedInteractionsCount > 0
          ? 'Confirmed high-risk interaction evidence was found'
          : 'Confirmed duplicate ingredient evidence was found',
      confirmedInteractionsCount,
    };
  }

  if (level === 'caution') {
    return {
      level,
      title: 'Caution',
      description: 'Some information needs verification or professional review',
      confirmedInteractionsCount,
    };
  }

  return {
    level,
    title: 'Safe',
    description: 'No confirmed interactions or duplicate ingredients detected',
    confirmedInteractionsCount,
  };
}

export async function analyzeVerifiedMedicines(
  medicines: Medicine[],
  ocrText?: string,
  modelUsage?: AiModelUsage
): Promise<AnalysisResult> {
  const correctedMedicines = resolveExtractedMedicines(ocrText || '', medicines);
  const verifiedMedicines = prepareVerifiedMedicines(correctedMedicines);
  const { interactions, model } = await checkDrugInteractions(verifiedMedicines);
  const usage = { ...modelUsage, interactionModel: model };
  const issues = generateSafetyIssues(verifiedMedicines, interactions);
  const safetyScore = calculateSafetyScore(verifiedMedicines, interactions);

  return {
    medicines: verifiedMedicines,
    interactions,
    issues,
    warnings: generateWarnings(issues),
    safetyScore,
    ocrText,
    modelUsage: usage,
    patientInfo: extractPatientInfo(ocrText || ''),
  };
}

export function getRiskExplanation(
  medicines: Medicine[],
  interactions: DrugInteraction[],
  _safetyScore: number,
  issues = generateSafetyIssues(medicines, interactions)
): RiskExplanation {
  const reasons = issues.map((issue) => issue.description);
  const tips = issues.map((issue) => issue.recommendation);
  const level = getRiskLevel(issues);

  if (tips.length === 0) {
    tips.push('Keep following the prescription exactly as written and ask a professional if anything feels unclear.');
  }

  return { reasons, tips: Array.from(new Set(tips)), level };
}
